/**
 * Visitor history helpers for the Analytics "History" view: yearly totals,
 * year-on-year month lines, year-to-date comparisons and the CSV export.
 * Rows come from GET /api/admin/stats/monthly — one per month with public /
 * local / bookings / ghan guests (null = not recorded) and the total.
 * The Umoona Manager app has a TypeScript twin in lib/visitorHistory.ts.
 */

export const CATEGORIES = [
  { key: "public", label: "Public tours", color: "#f15a24" },
  { key: "local", label: "Local (Noble & Oasis)", color: "#10b981" },
  { key: "bookings", label: "Other tour groups", color: "#7c4dff" },
  { key: "ghan", label: "The Ghan", color: "#d97706" },
];

export const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Guests for a row in one category, or all categories ("all"); null if nothing was recorded. */
export function valueOf(row, category) {
  if (!row) return null;
  if (category === "all") return row.total;
  return row[category];
}

/** [{year, public, local, bookings, ghan, total, partial}] */
export function yearTotals(rows, currentMonth) {
  const years = new Map();
  for (const row of rows) {
    const year = Number(row.month.slice(0, 4));
    if (!years.has(year)) years.set(year, { year, public: 0, local: 0, bookings: 0, ghan: 0, total: 0, recorded: false });
    const y = years.get(year);
    for (const c of CATEGORIES) {
      if (row[c.key] != null) {
        y[c.key] += row[c.key];
        y.total += row[c.key];
        y.recorded = true;
      }
    }
  }
  return [...years.values()]
    .filter((y) => y.recorded)
    .map(({ recorded, ...y }) => ({ ...y, partial: currentMonth && String(y.year) === currentMonth.slice(0, 4) }));
}

/** Recharts data for Jan–Dec with one key per year: [{month: "Jan", 2019: 677, 2025: 339}] */
export function yearOnYear(rows, years, category) {
  const byMonth = new Map(rows.map((r) => [r.month, r]));
  return MONTH_NAMES.map((name, i) => {
    const point = { month: name };
    for (const year of years) {
      const value = valueOf(byMonth.get(`${year}-${String(i + 1).padStart(2, "0")}`), category);
      point[year] = value == null ? null : value;
    }
    return point;
  });
}

const sumMonths = (rows, year, lastMonth, category) => {
  let total = 0;
  let any = false;
  for (const row of rows) {
    if (row.month.slice(0, 4) !== String(year) || Number(row.month.slice(5, 7)) > lastMonth) continue;
    const v = valueOf(row, category);
    if (v != null) {
      total += v;
      any = true;
    }
  }
  return any ? total : null;
};

/**
 * This year to date (to the last full month, or the current month if
 * includeCurrent) against the same months last year and the 2015–2019
 * pre-COVID average.
 */
export function yearToDate(rows, currentMonth, category, includeCurrent = true) {
  const year = Number(currentMonth.slice(0, 4));
  const lastMonth = Number(currentMonth.slice(5, 7)) - (includeCurrent ? 0 : 1);
  const thisYear = lastMonth > 0 ? sumMonths(rows, year, lastMonth, category) : null;
  const lastYear = lastMonth > 0 ? sumMonths(rows, year - 1, lastMonth, category) : null;
  const baseline = [2015, 2016, 2017, 2018, 2019].map((y) => sumMonths(rows, y, lastMonth, category)).filter((v) => v != null);
  const average = baseline.length ? Math.round(baseline.reduce((a, b) => a + b, 0) / baseline.length) : null;
  return { year, lastMonth, thisYear, lastYear, average };
}

export const pctChange = (now, before) => (now == null || !before ? null : ((now - before) / before) * 100);

/** Best month and best year on record for a category. */
export function records(rows, years, category) {
  let bestMonth = null;
  for (const row of rows) {
    const v = valueOf(row, category);
    if (v != null && (!bestMonth || v > bestMonth.value)) bestMonth = { month: row.month, value: v };
  }
  const key = category === "all" ? "total" : category;
  const complete = years.filter((y) => !y.partial);
  const bestYear = complete.reduce((best, y) => (!best || y[key] > best[key] ? y : best), null);
  return { bestMonth, bestYear: bestYear ? { year: bestYear.year, value: bestYear[key] } : null };
}

export const monthLabel = (month) => `${MONTH_NAMES[Number(month.slice(5, 7)) - 1]} ${month.slice(0, 4)}`;

const csvCell = (v) => {
  const s = v == null ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export function historyCsv(rows) {
  const head = ["Month", ...CATEGORIES.map((c) => c.label), "Total", "Source", "Estimated", "Notes"];
  const lines = rows.map((r) => [
    r.month,
    ...CATEGORIES.map((c) => r[c.key]),
    r.total,
    r.from === "archive" ? "Archive spreadsheet" : "Booking system",
    r.estimated ? "Yes" : "",
    Object.entries(r.notes || {}).map(([k, v]) => `${k}: ${v}`).join("; "),
  ]);
  return [head, ...lines].map((line) => line.map(csvCell).join(",")).join("\n");
}

/** "2026-10" for today's month in Adelaide. */
export function adelaideMonth(date = new Date()) {
  const parts = new Intl.DateTimeFormat("en-AU", { timeZone: "Australia/Adelaide", year: "numeric", month: "2-digit" }).formatToParts(date);
  const get = (t) => parts.find((p) => p.type === t).value;
  return `${get("year")}-${get("month")}`;
}
